import { ArrowDown, ArrowUp } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "./cn.ts";

export interface DataColumn {
  readonly id: string;
  readonly header: string;
  /** Sắp xếp được (field indexed); `sort` là chiều hiện tại của cột này. */
  readonly sortable?: boolean;
  readonly sort?: "asc" | "desc" | null;
}

export interface DataRow {
  readonly id: string;
  readonly cells: readonly ReactNode[];
}

/** Bảng thật (`<table>`), `aria-sort` trên cột đang sắp xếp; nút tiêu đề đổi chiều. Chữ do nơi gọi truyền (i18n). */
export function DataTable({ caption, columns, rows, onSort, sortLabel }: { caption: string; columns: readonly DataColumn[]; rows: readonly DataRow[]; onSort?: (columnId: string) => void; sortLabel: (header: string) => string }) {
  return (
    <div tabIndex={0} role="region" aria-label={caption} className="focus-visible:outline-2 overflow-x-auto rounded-lg border border-(--saa-border) bg-(--saa-surface)">
      <table className="w-full text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="border-b border-(--saa-border) text-left text-(--saa-fg-muted)">
          <tr>
            {columns.map((c) => (
              <th key={c.id} scope="col" aria-sort={c.sort === "asc" ? "ascending" : c.sort === "desc" ? "descending" : undefined} className="px-4 py-2 font-medium">
                {c.sortable && onSort ? (
                  <button type="button" className="inline-flex items-center gap-1 hover:underline" aria-label={sortLabel(c.header)} onClick={() => onSort(c.id)}>
                    {c.header}
                    {c.sort === "asc" ? <ArrowUp size={12} aria-hidden /> : c.sort === "desc" ? <ArrowDown size={12} aria-hidden /> : null}
                  </button>
                ) : (
                  c.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={cn("border-b border-(--saa-border) last:border-0 focus-within:bg-(--saa-muted)")}>
              {r.cells.map((cell, i) => (
                <td key={columns[i]?.id ?? i} className="px-4 py-2">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
